/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    DocAddonBridge,
    DocData, FileMetaInfo, FileType,
    DocService, AddonInfo, Action, ActionName,
    MenuType, ContentContainer,
} from '@tencent/td-addon-sdk-docs';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import AddonAbility from './addon-ability';
import { AddonManagerEventsMap, IAddonManager } from './addon-manager.interface';
import { IAddonHandlerBase } from '@src/slide/application/service/addon/addon-handler-base.interface';
import DUILoading from '@tencent/dui/lib/components/Loading';
import i18n from '@i18next/i18n';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import ImageUrl from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IEventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';

export default abstract class BaseAddonHandler implements IAddonHandlerBase {
    public addonContentContainerMap: Map<string, ContentContainer|undefined> = new Map();
    // key: 插件id, value: 新的插件数据结构
    public addonInfoMap: Map<string, AddonInfo> = new Map();
    // key: 插件id, value: 旧的插件数据结构
    public addonMetaMap: Map<string, any> = new Map();

    protected addonAbility: AddonAbility;
    protected isLoadAddonInfoList = false;

    // 插件实例
    protected docAddonBridge: any;
    protected event: IEventEmitter<AddonManagerEventsMap>;

    // 实例化
    public constructor(protected api: any, addonManager: IAddonManager, protected selectionService: ISelectionService) {
        const clientVars = SlideDataServices.ClientVarsService()?.getClientVars?.();
        this.event = this.api.event;
        this.addonAbility = new AddonAbility(addonManager, selectionService);
        // 初始化插件
        this.docAddonBridge = DocAddonBridge;
        if (clientVars) {
            this.getAddonInfo();
        }

        SlideDataServices.ClientVarsService.promise().then((service) => {
            if (!service.getClientVars()) this.getAddonInfo();
        });
    }

    /**
     * 触发菜单插件能力
     * @param addonId
     * @param menuID
     */
    public executeMenuAction(addonId: string, menuID: string): Promise<void> {
        const menuIndex = menuID?.split('_')?.[1];
        const menuItem = this.addonMetaMap.get(addonId)?.config?.menus?.[menuIndex];
        const actionStore: Action = menuItem?.actionStore;

        return this.docAddonBridge.executeMenuAction(addonId, actionStore)
            .catch((err: any) => {
                Snackbar.show({ message: err, autoClose: true, type: 'error' });
            });
    }

    public async openAddonPanel(addonId, closeAddonPanel?) {
        const addon: AddonInfo = await DocAddonBridge.getAddon(addonId, { shouldGetLatest: true });
        const selectedModel: any = this.selectionService.getSelectedModel();
        let loading;
        if (selectedModel?.type === UIModelType.IMAGE) {
            const src = selectedModel.props?.src || '';
            if (ImageUrl.isBlobUrl(src)) {
                const ImageResource = ImageQuickLookManager.getInstance().getResourceByUrl(src);
                const cdnUrl = ImageResource?.getCdnUrl();
                if (!cdnUrl) {
                    loading = DUILoading.show({ tip: i18n.t('正在插入图片...'), zIndex: 11001 });
                }
            }
        }
        await DocAddonBridge.fetchAddonMenu(addonId);
        loading?.close();
        await DocAddonBridge.executeMenuAction(addonId, addon.menuConfiguration as Action);
        if (closeAddonPanel) {
            await closeAddonPanel();
        }
    }

    // 获取插件初始化配置
    protected getDocData(): DocData {
        return {
            metaInfo: this.getGeneralParams() as FileMetaInfo,
            service: this.addonAbility.docService as DocService,
        };
    }

    /**
     * 初始化插件配置
     * @param docData
     */
    protected initAddonOptions(docData: DocData): void {
        this.docAddonBridge.setup(docData);
    }

    /**
     * 获取兼容化处理的插件数据
     * @param addonInfo
     * @returns
     */
    protected getCompatibleAddon(addonInfo: AddonInfo): any {
        const actionType: any = {
            [ActionName.DIALOG]: 2,
        };
        const { menuConfiguration } = addonInfo;
        const menus: any = [];
        if (Array.isArray(menuConfiguration)) {
            let index = 0;
            let cateIndex = 0;
            menuConfiguration.forEach((menuItem: any) => {
                if (menuItem.type === MenuType.DIVIDE) {
                    cateIndex += 1;
                    return;
                }
                const action: any = menuItem?.action;
                menus.push({
                    action: actionType[action?.name],
                    height: action?.options.height,
                    icon: menuItem.icon,
                    id: `${addonInfo.addonId}_${index}`,
                    name: action?.options.title,
                    page: action?.options.src,
                    report_id: null,
                    title: menuItem.title,
                    width: action?.options.width,
                    actionStore: action,
                    category: `cate-${cateIndex}`,
                });
                index += 1;
            });
        } else {
            const action: any = menuConfiguration;
            menus.push({
                action: actionType[action?.name],
                height: action?.options.height,
                icon: addonInfo.icons.small,
                id: `${addonInfo.addonId}_0`,
                name: addonInfo.name,
                page: action?.options.src,
                report_id: null,
                title: action?.options.title,
                width: action?.options.width,
                actionStore: action,
                description: addonInfo.shortDesc,
                subtitle: addonInfo.vendorName,
                imageUrl: addonInfo.icons.intro,
            });
        }

        const data: any = {};
        data.aid = addonInfo.addonId;
        data.config = {
            domains: addonInfo?.config?.domains || null,
            menus,
            name: addonInfo.name,
        };
        data.file_path = `//${addonInfo.staticHost}/${addonInfo.staticPath}`;
        return data;
    }


    // 获取一些常用参数
    protected getGeneralParams(): FileMetaInfo {
        const clientVars = SlideDataServices.ClientVarsService()?.getClientVars?.();
        const creatorId = clientVars?.creatorId || '';
        const uid = clientVars?.userInfo?.uid || '';
        return {
            fileId: (clientVars?.globalPadId || '') as string,
            fileType: FileType.SLIDE as number,
            creatorId,
            uid,
        };
    }

    // 缓存插件源数据
    protected saveAddonInfo(addonInfo: AddonInfo): void {
        this.addonInfoMap.set(addonInfo.addonId, addonInfo);
    }

    // 缓存兼容化插件数据
    protected saveAddonMeta(addonMeta: any): void {
        this.addonMetaMap.set(addonMeta.aid, addonMeta);
    }

    protected saveContentContainer(addonId: string, contentContainer: ContentContainer|undefined): void {
        this.addonContentContainerMap.set(addonId, contentContainer);
    }

    // 获取插件源数据列表
    protected fetchAddonInfoList(): Promise<AddonInfo[]> {
        return this.docAddonBridge.fetchAddonList();
    }

    // 根据插件id获取该插件的toolbar buttons和action等数据信息
    protected async fetchContentContainer(addonId: string): Promise<ContentContainer|undefined> {
        const contentContainer = (
            await this.docAddonBridge.fetchContentContainerConfiguration(addonId)
        ) as ContentContainer;
        this.saveContentContainer(addonId, contentContainer);

        return contentContainer;
    }

    protected abstract initAddonInfoList(): any;

    protected abstract handleFetchAddonInfoList(addonInfoList: AddonInfo[]): void;

    protected abstract getAddonInfo(): void;
}
